import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import GalleryService from '@clickersphere/core/services/GalleryService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { GALLERIES_QUERY_KEY } from './useGalleries';
import { GalleryDetailQueryData, buildGalleryDetailKey } from './useGallery';

interface IRemovePhotoParams {
  photoId: string;
}

export function useRemoveGalleryPhoto(galleryId: string) {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { isPending: removePhotoIsPending, mutateAsync: removePhotoAsync } =
    useMutation({
      mutationFn: ({ photoId }: IRemovePhotoParams) =>
        GalleryService.removePhoto(galleryId, photoId),
      onMutate: async ({ photoId }) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(buildGalleryDetailKey(galleryId)),
        });

        const oldGallery = queryClient.getQueryData<GalleryDetailQueryData>(
          buildQueryKey(buildGalleryDetailKey(galleryId)),
        );

        queryClient.setQueryData<GalleryDetailQueryData>(
          buildQueryKey(buildGalleryDetailKey(galleryId)),
          (old) =>
            old
              ? {
                  ...old,
                  photos: old.photos?.filter((photo) => photo.id !== photoId),
                }
              : old,
        );

        return { oldGallery };
      },
      onSuccess: () => {
        const detail = queryClient.getQueryData<GalleryDetailQueryData>(
          buildQueryKey(buildGalleryDetailKey(galleryId)),
        );
        if (detail?.leadId) {
          void queryClient.invalidateQueries({
            queryKey: buildQueryKey([GALLERIES_QUERY_KEY, detail.leadId]),
          });
        }
        toast(t('gallery.toasts.photoRemoved'), 'success');
      },
      onError: (_error, _params, context) => {
        queryClient.setQueryData(
          buildQueryKey(buildGalleryDetailKey(galleryId)),
          context?.oldGallery,
        );
        toast(t('gallery.toasts.photoRemoveError'), 'error');
      },
    });

  return {
    removePhotoAsync,
    removePhotoIsPending,
  };
}
